import { PageTitle as SharedPageTitle } from '../../../components/layout/PageHeader';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useSmartSortingSummary } from '../hooks/useSmartSortingSummary';
import { useSmartSortingUploads } from '../hooks/useSmartSortingUploads';
import { SmartSortingEmptyState } from './SmartSortingEmptyState';
import { SmartSortingUploadPanel } from './SmartSortingUploadPanel';
import { SmartSortingUploadQueue } from './SmartSortingUploadQueue';
import { SmartSortingCategoryGrid } from './SmartSortingCategoryGrid';

const Container = styled.div<{ $embedded: boolean }>`
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
`;

const Header = styled.header`
  margin-bottom: 2rem;
`;

const Title = styled(SharedPageTitle)``;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const ProcessingStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--brand-50, #eff6ff);
  border: 1px solid var(--brand-200, #bfdbfe);
  border-radius: var(--radius-md);
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: var(--brand-700, #1d4ed8);
`;

const FailedStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: var(--danger-50, #fef2f2);
  border: 1px solid var(--danger-100, #fecaca);
  border-radius: var(--radius-md);
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: var(--danger-600, #b91c1c);

  & > span {
    flex: 1;
  }
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  color: var(--danger-600, #b91c1c);
  font-size: 0.8rem;
  text-decoration: underline;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  white-space: nowrap;

  &:hover {
    color: var(--danger-600, #7f1d1d);
  }

  &:focus-visible {
    outline: 2px solid var(--danger-600, #b91c1c);
    outline-offset: 2px;
    border-radius: var(--radius-sm);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
`;

const Spinner = styled.span`
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid var(--border);
  border-top-color: var(--brand-500);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin-right: 0.5rem;
`;

interface SmartSortingViewProps {
  embedded?: boolean;
}

export const SmartSortingView: React.FC<SmartSortingViewProps> = ({
  embedded = false,
}) => {
  const { data: summary, isLoading, isError } = useSmartSortingSummary();
  const { queue, addFiles, clearCompleted, isUploading } =
    useSmartSortingUploads();
  const [failedDismissed, setFailedDismissed] = React.useState(false);

  const prevFailedCount = React.useRef(summary?.failed_count ?? 0);
  React.useEffect(() => {
    const current = summary?.failed_count ?? 0;
    if (current !== prevFailedCount.current) {
      setFailedDismissed(false);
      prevFailedCount.current = current;
    }
  }, [summary?.failed_count]);

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      const errors = addFiles(files);
      if (errors.length > 0) {
        errors.forEach((err) => toast.error(err));
      }
    },
    [addFiles],
  );

  if (isLoading) {
    return (
      <Container $embedded={embedded}>
        <LoadingContainer>
          <Spinner />
          Cargando...
        </LoadingContainer>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container $embedded={embedded}>
        <LoadingContainer>
          Error al cargar la información. Intenta recargar la página.
        </LoadingContainer>
      </Container>
    );
  }

  const hasFiles = summary?.has_files ?? false;
  const showEmptyState = !hasFiles && !isUploading && queue.length === 0;

  return (
    <Container $embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>Clasificación Inteligente</Title>
          <Subtitle>
            Sube documentos y los clasificaremos automáticamente con IA
          </Subtitle>
        </Header>
      )}

      {showEmptyState ? (
        <SmartSortingEmptyState />
      ) : (
        <>
          <Section>
            <SmartSortingUploadPanel
              compact={hasFiles}
              onFilesSelected={handleFilesSelected}
            />
            <SmartSortingUploadQueue
              queue={queue}
              onClearCompleted={clearCompleted}
            />
          </Section>

          {(summary?.processing_count ?? 0) > 0 && (
            <ProcessingStrip>
              <Spinner />
              {summary?.processing_count ?? 0} archivo(s) procesándose...
            </ProcessingStrip>
          )}

          {(summary?.failed_count ?? 0) > 0 && !failedDismissed && (
            <FailedStrip>
              <span>
                {summary?.failed_count ?? 0} archivo(s) fallaron al
                clasificarse.
              </span>
              <DismissButton
                type="button"
                onClick={() => setFailedDismissed(true)}
                aria-label="Descartar alerta de archivos fallidos"
              >
                Descartar
              </DismissButton>
            </FailedStrip>
          )}

          {summary && summary.categories.length > 0 && (
            <Section>
              <SmartSortingCategoryGrid categories={summary.categories} />
            </Section>
          )}
        </>
      )}

      {showEmptyState && (
        <SmartSortingUploadPanel
          compact={false}
          onFilesSelected={handleFilesSelected}
        />
      )}

      {showEmptyState && queue.length > 0 && (
        <SmartSortingUploadQueue
          queue={queue}
          onClearCompleted={clearCompleted}
        />
      )}
    </Container>
  );
};
